// Dono: gap-api. /api-e-agentes: criar chave de API (nome, escrita, privados, uma coleção), mostrar uma vez, listar e
// revogar; endereço do MCP com guias curtos por agente; referência (docs/api.md) renderizada aqui.
// Esqueleto da tela de extensões da referência (o mesmo de /aparelhos); textos, guias e desenho nossos.
import "../../styles/account.css";
import "../../styles/api.css";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { ApiKeyResponse, Device, Space } from "../../../../shared/types.ts";
import reference from "../../../../docs/api.md?raw";
import { IconCopy, IconTrash, Mark } from "../Icons.tsx";
import { relativeTime } from "../lib.ts";
import { IconKey } from "../share/icons.tsx";
import { showModal, store, toast } from "../state.ts";
import { BackLink, Confirm, follow } from "./AccountPage.tsx";

type Agent = "claude" | "chatgpt" | "outros";
const AGENTS: [Agent, string][] = [["claude", "Claude"], ["chatgpt", "ChatGPT"], ["outros", "Outros"]];
const PLACEHOLDER = "faig_SUA_CHAVE";
const keyName = (d: Device) => d.label?.trim() || "Chave sem nome";
const access = (d: Device) => `${d.scopes.includes("write") ? "Leitura e escrita" : "Só leitura"}${d.scopes.includes("private") ? ", com privados" : ""}`;
const spaceOf = (d: Device) => d.scopes.find((s) => s.startsWith("spaces:"))?.slice(7) ?? null;

function copy(text: string, what: string) {
  const done = navigator.clipboard?.writeText(text);
  if (!done) return toast({ key: "api-copy-fail", text: "Não deu para copiar. Selecione e copie à mão." });
  done.then(
    () => toast({ key: "api-copy", text: `${what} copiado.` }),
    () => toast({ key: "api-copy-fail", text: "Não deu para copiar. Selecione e copie à mão." }),
  );
}

/** POST /v1/devices/keys (só a sessão web cria chave). Falha vira aviso; devolve null. */
async function createKey(body: { label: string; write: boolean; private: boolean; spaceId?: string }): Promise<ApiKeyResponse | null> {
  try {
    const res = await fetch("/v1/devices/keys", { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
    if (res.ok) return (await res.json()) as ApiKeyResponse;
    const p = (await res.json().catch(() => null)) as { title?: unknown } | null;
    toast({ key: "api-key-fail", text: res.status === 401 ? "Sua sessão terminou. Entre de novo." : typeof p?.title === "string" ? p.title : "Não deu para criar a chave. Tente de novo." });
  } catch {
    toast({ key: "api-key-fail", text: "Sem conexão com o servidor. Tente de novo." });
  }
  return null;
}

/** Bloco de comando ou configuração com botão de copiar. */
function Snippet({ text, what }: { text: string; what: string }) {
  return (
    <div class="api-snippet">
      {/* tabIndex: no celular o bloco rola de lado (não quebra no meio de --header) e o teclado também rola. */}
      <pre tabIndex={0}>
        <code>{text}</code>
      </pre>
      <button type="button" class="api-copy" aria-label={`Copiar ${what.toLowerCase()}`} data-tip="Copiar" onClick={() => copy(text, what)}>
        <IconCopy />
      </button>
    </div>
  );
}

function Guide({ agent, url }: { agent: Agent; url: string }) {
  const header = `Authorization: Bearer ${PLACEHOLDER}`;
  // Argumentos numa linha só: o bloco fica curto o bastante para ler no celular.
  const remote = `{
  "mcpServers": {
    "faig": {
      "command": "npx",
      "args": ${JSON.stringify(["-y", "mcp-remote", url, "--header", header]).replaceAll('","', '", "')}
    }
  }
}`;
  if (agent === "claude") {
    return (
      <>
        <h3>Claude Code</h3>
        <p>No terminal, troque a chave de exemplo pela sua:</p>
        <Snippet what="Comando" text={`claude mcp add --transport http faig ${url} --header "${header}"`} />
        <h3>Claude Desktop</h3>
        <p>
          Em <strong>Configurações → Desenvolvedor → Editar configuração</strong>, acrescente o servidor abaixo e reabra o app. Precisa do Node instalado.
        </p>
        <Snippet what="Configuração" text={remote} />
        <p class="dev-muted">O conector personalizado do Claude no navegador entra por OAuth, que o fai.g ainda não oferece.</p>
      </>
    );
  }
  if (agent === "chatgpt") {
    return (
      <>
        <h3>Conector (modo desenvolvedor)</h3>
        <p>
          Em <strong>Configurações → Conectores</strong>, ligue o modo desenvolvedor e crie um conector com o endereço acima. Na autenticação, use o cabeçalho{" "}
          <code>Authorization</code> com <code>Bearer</code> e a sua chave.
        </p>
        <h3>Se a sua conta só oferecer OAuth</h3>
        <p>
          Crie um GPT com uma <strong>Ação</strong> para a API REST (<code>{location.origin}/v1</code>), autenticação <strong>Chave de API</strong> do tipo{" "}
          <strong>Bearer</strong>, e cole a mesma chave. As rotas estão na referência abaixo.
        </p>
      </>
    );
  }
  return (
    <>
      <h3>Cursor, VS Code, Poke, Hermes e outros</h3>
      <p>A maioria dos clientes MCP aceita um servidor remoto com endereço e cabeçalho:</p>
      <Snippet what="Configuração" text={JSON.stringify({ mcpServers: { faig: { url, headers: { Authorization: `Bearer ${PLACEHOLDER}` } } } }, null, 2)} />
      <h3>Agentes locais</h3>
      <p>
        Quem só fala MCP pela linha de comando usa o <code>mcp-remote</code>, como no Claude Desktop:
      </p>
      <Snippet what="Configuração" text={remote} />
    </>
  );
}

export function ApiPage() {
  const online = store.status.value.online;
  const spaces = store.spaces.value.filter((s) => s.kind === "manual");
  const [keys, setKeys] = useState<Device[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [agent, setAgent] = useState<Agent>("claude");
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<Device | null>(null);
  const [asking, setAsking] = useState(false);
  const ref = useRef<HTMLDetailsElement>(null);
  const url = `${location.origin}/mcp`;
  const html = useMemo(() => {
    const md = reference.replaceAll("https://app.faig.app", location.origin);
    // Blocos de código rolam de lado no celular: com tabindex o teclado também rola.
    return DOMPurify.sanitize(marked.parse(md, { async: false, gfm: true }) as string).replaceAll("<pre>", '<pre tabindex="0">');
  }, []);

  const load = () =>
    store.devices().then(
      (d) => (setKeys(d.filter((x) => x.kind === "api")), setFailed(false)),
      () => setFailed(true),
    );
  useEffect(() => void (online && load()), [online]);
  useEffect(() => scrollTo(0, 0), []);

  const openRef = () => {
    const d = ref.current!;
    d.open = true;
    d.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
    d.querySelector("summary")?.focus({ preventScroll: true });
  };
  const revoke = (d: Device) =>
    store.revokeDevice(d.id).then(
      () => {
        setKeys((l) => l?.filter((x) => x.id !== d.id) ?? null);
        toast({ key: `revoked:${d.id}`, text: `${keyName(d)} foi revogada. Quem a usava perdeu o acesso.` });
      },
      () => void load(),
    );
  const spaceName = (d: Device) => {
    const id = spaceOf(d);
    if (!id) return "Todo o acervo";
    const s = store.spaces.value.find((x) => x.id === id);
    return s ? `Coleção ${s.name}` : "Coleção apagada";
  };

  return (
    <main id="main" tabIndex={-1} class="page dev api">
      <BackLink />
      <article class="dev-wrap">
        <h1 class="dev-title">API e agentes</h1>

        <section class="dev-card dev-welcome" aria-labelledby="api-welcome">
          <span class="dev-mark">
            <Mark />
          </span>
          <div class="dev-welcome-body">
            <h2 id="api-welcome">
              Seu acervo nos seus programas <span class="acc-badge">beta</span>
            </h2>
            <p>Com uma chave, outros programas e agentes de IA buscam, leem e guardam no seu fai.g sem passar pelo app.</p>
            <p>Cada chave leva só a permissão que você der: ler, escrever, ver os cards privados ou ficar presa a uma coleção. Ela aparece uma vez e pode ser revogada aqui.</p>
            <ul class="dev-tiles">
              <li>
                <button type="button" onClick={openRef}>
                  <strong>Chaves</strong>
                  <span>Bearer no cabeçalho, com escopos que valem de verdade.</span>
                </button>
              </li>
              <li>
                <button type="button" onClick={openRef}>
                  <strong>Limites</strong>
                  <span>120 pedidos por minuto, com cabeçalhos RateLimit.</span>
                </button>
              </li>
              <li>
                <button type="button" onClick={openRef}>
                  <strong>Markdown</strong>
                  <span>Notas entram e saem em Markdown, JSON ou texto.</span>
                </button>
              </li>
            </ul>
          </div>
        </section>

        <section class="dev-card api-mcp" aria-labelledby="api-mcp">
          <div class="api-head">
            <div>
              <h2 id="api-mcp" class="dev-label">
                MCP
              </h2>
              <p class="dev-muted">Use o seu acervo direto no seu agente.</p>
            </div>
            <div class="acc-seg" role="group" aria-label="Guia para o agente">
              {AGENTS.map(([id, name]) => (
                <button key={id} type="button" aria-pressed={agent === id} aria-controls="api-guide" onClick={() => setAgent(id)}>
                  {name}
                </button>
              ))}
            </div>
          </div>
          <p class="api-label" id="api-url-label">
            Endereço do servidor
          </p>
          <div class="api-url" aria-labelledby="api-url-label">
            <code>{url}</code>
            <button type="button" class="api-copy" aria-label="Copiar endereço do MCP" data-tip="Copiar" onClick={() => copy(url, "Endereço")}>
              <IconCopy />
            </button>
          </div>
          <div id="api-guide" class="api-guide" aria-label={`Guia: ${AGENTS.find(([id]) => id === agent)![1]}`} role="region">
            <Guide agent={agent} url={url} />
          </div>
          <p class="dev-muted api-tip">Prefira uma chave só de leitura. Ligue a escrita só para o agente que vai guardar coisas por você.</p>
        </section>

        <section class="dev-card dev-list" aria-labelledby="api-keys">
          <div class="api-head">
            <h2 id="api-keys" class="dev-label">
              Suas chaves
            </h2>
            {keys?.length ? (
              <button type="button" class="dev-dark" disabled={!online} onClick={() => setCreating(true)}>
                Criar chave
              </button>
            ) : null}
          </div>
          {keys === null ? (
            <p class="dev-muted" role="status">
              {failed || !online ? "Sem conexão: a lista aparece quando a rede voltar." : "Carregando…"}
            </p>
          ) : keys.length === 0 ? (
            <div class="dev-empty api-empty">
              <span class="dev-empty-icon">
                <IconKey />
              </span>
              <div>
                <p class="dev-strong">Nenhuma chave ainda</p>
                <p class="dev-muted">Uma chave deixa outro programa ou agente usar o seu acervo, com a permissão que você escolher.</p>
              </div>
              <button type="button" class="acc-btn primary" disabled={!online} onClick={() => setCreating(true)}>
                Criar a primeira chave
              </button>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Acesso</th>
                  <th scope="col">Alcance</th>
                  <th scope="col">Último uso</th>
                  <th scope="col">
                    <span class="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {keys.map((d) => (
                  <tr key={d.id}>
                    <th scope="row">
                      <IconKey />
                      {keyName(d)}
                    </th>
                    <td data-label="Acesso">{access(d)}</td>
                    <td data-label="Alcance">{spaceName(d)}</td>
                    <td data-label="Último uso">{d.lastSeenAt ? relativeTime(d.lastSeenAt) : "nunca usada"}</td>
                    <td>
                      <button
                        type="button"
                        class="dev-revoke"
                        aria-label={`Revogar ${keyName(d)}`}
                        data-tip="Revogar"
                        onClick={() => {
                          setRevoking(d);
                          setAsking(true);
                        }}
                      >
                        <IconTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section class="dev-card api-ref" aria-labelledby="api-ref">
          <h2 id="api-ref" class="dev-label">
            Referência
          </h2>
          <p class="dev-muted">Rotas, filtros, formatos, erros e o MCP.</p>
          <details ref={ref}>
            <summary>Ler a referência completa</summary>
            <div class="api-doc" dangerouslySetInnerHTML={{ __html: html }} />
          </details>
        </section>

        <footer class="dev-foot api-foot">
          <a href="/conta" onClick={follow("/conta")}>
            Conta e preferências
          </a>
          <a href="/aparelhos" onClick={follow("/aparelhos")}>
            Aparelhos e extensões
          </a>
        </footer>
      </article>
      <NewKey open={creating} online={online} spaces={spaces} onClose={() => setCreating(false)} onCreated={(k) => setKeys((l) => [k, ...(l ?? [])])} />
      <Confirm
        id="api-revoke"
        open={asking}
        title={`Revogar ${revoking ? keyName(revoking) : "chave"}?`}
        action="Revogar"
        onClose={() => setAsking(false)}
        onConfirm={() => revoking && void revoke(revoking)}
      >
        <p>O próximo pedido feito com ela vai falhar, na API e no MCP. Para voltar a usar, crie outra chave.</p>
      </Confirm>
    </main>
  );
}

/** Diálogo de chave nova: formulário e, depois de criar, a chave uma única vez. */
function NewKey(p: { open: boolean; online: boolean; spaces: Space[]; onClose: () => void; onCreated: (k: Device) => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [label, setLabel] = useState("");
  const [write, setWrite] = useState(false);
  const [priv, setPriv] = useState(false);
  const [space, setSpace] = useState("");
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<ApiKeyResponse | null>(null);
  const copyRef = useRef<HTMLButtonElement>(null);
  // O formulário some ao criar: o foco vai para o copiar da chave (autofocus só vale ao abrir o diálogo).
  useEffect(() => void (made && copyRef.current?.focus()), [made]);
  useEffect(() => {
    const d = ref.current!;
    if (p.open && !d.open) {
      setLabel("");
      setWrite(false);
      setPriv(false);
      setSpace("");
      setMade(null);
      showModal(d);
    }
    if (!p.open && d.open) d.close();
  }, [p.open]);
  // A chave só vive no diálogo: fechar apaga da tela (e da memória da página).
  const close = () => ref.current?.close();
  const submit = async (e: Event) => {
    e.preventDefault();
    if (!label.trim() || busy) return;
    setBusy(true);
    const r = await createKey({ label: label.trim(), write, private: priv, ...(space ? { spaceId: space } : {}) });
    setBusy(false);
    if (!r) return;
    setMade(r);
    p.onCreated(r.key);
  };
  return (
    <dialog ref={ref} class="acc-confirm api-new" aria-labelledby="api-new-title" onClose={() => (setMade(null), p.onClose())}>
      {made ? (
        <div class="acc-confirm-box">
          <h2 id="api-new-title">Chave criada</h2>
          <p class="api-note" id="api-new-once">
            Copie agora e guarde num lugar seguro: ela não aparece de novo.
          </p>
          <div class="api-url api-secret" aria-describedby="api-new-once">
            <code>{made.token}</code>
            <button ref={copyRef} type="button" class="api-copy" aria-label="Copiar a chave" data-tip="Copiar" onClick={() => copy(made.token, "Chave")}>
              <IconCopy />
            </button>
          </div>
          <div class="acc-confirm-actions">
            <button type="button" class="acc-btn primary" onClick={close}>
              Pronto
            </button>
          </div>
        </div>
      ) : (
        <form class="acc-confirm-box" onSubmit={submit}>
          <h2 id="api-new-title">Nova chave</h2>
          <label class="api-field">
            <span>Nome</span>
            <input
              type="text"
              value={label}
              maxLength={100}
              required
              autofocus
              placeholder="Ex.: Claude do notebook"
              aria-describedby="api-name-hint"
              onInput={(e) => setLabel(e.currentTarget.value)}
            />
          </label>
          <p id="api-name-hint" class="api-hint">
            Um nome que lembre onde a chave vai ser usada.
          </p>
          <fieldset class="api-access">
            <legend>Acesso</legend>
            <label class={write ? "" : "is-on"}>
              <input type="radio" name="api-access" checked={!write} onChange={() => setWrite(false)} />
              <strong>Só leitura</strong>
              <span>Busca e lê cards, coleções e etiquetas.</span>
            </label>
            <label class={write ? "is-on" : ""}>
              <input type="radio" name="api-access" checked={write} onChange={() => setWrite(true)} />
              <strong>Leitura e escrita</strong>
              <span>Também guarda, edita, etiqueta e apaga.</span>
            </label>
          </fieldset>
          {write && <p class="api-note">Com escrita, quem tiver a chave pode mudar e apagar cards. Dê só a programas e agentes em que você confia.</p>}
          <label class="api-check">
            <input type="checkbox" checked={priv} onChange={(e) => setPriv(e.currentTarget.checked)} />
            <span>
              Incluir cards privados <span class="dev-muted">(#privado)</span>
            </span>
          </label>
          <label class="api-field">
            <span>Alcance</span>
            <select value={space} onChange={(e) => setSpace(e.currentTarget.value)}>
              <option value="">Todo o acervo</option>
              {p.spaces.map((s) => (
                <option key={s.id} value={s.id}>
                  Só a coleção {s.name}
                </option>
              ))}
            </select>
          </label>
          <div class="acc-confirm-actions">
            <button type="button" class="acc-btn" onClick={close}>
              Cancelar
            </button>
            <button type="submit" class="acc-btn primary" disabled={busy || !label.trim() || !p.online}>
              {busy ? "Criando…" : "Criar chave"}
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}
